import type {
  BrandSpecials,
  MaterializedMenuProduct,
} from '@iheartjane/data-access';
import type { SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import { Flex, Typography } from '@iheartjane/reefer';
import type {
  ComplianceLanguageSettings,
  LabeledOption,
  Nullable,
} from '@iheartjane/types';
import { useMemo } from 'react';

import { DealsBanner } from '@jane/bloom/components/dealsBanner';
import { SpecialsGrid } from '@jane/bloom/components/menuRow/specialsGrid';
import {
  FilterSelector,
  MobileFilterRow,
} from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors';
import { ProductGrid } from '@jane/bloom/components/productListingPage/productGrid';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useShowInlineWizardCta } from '@jane/bloom/hooks/useShowInlineWizardCta';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { WizardOpenSource } from '@jane/bloom/services/tracking/Events';
import { WIZARD_SOURCE } from '@jane/bloom/services/tracking/Events';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { sortSpecials } from '@jane/bloom/utils/specials/sortSpecials';
import { formatProductCount } from '@jane/bloom/utils/strings/formatProductCount';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';

import styles from './dealsTab.module.css';

interface DealsTabProps {
  brandSpecials: BrandSpecials;
  complianceLanguage?: Nullable<ComplianceLanguageSettings>;
  currentSort: SortOptions;
  customLanguage: CustomLanguage;
  enabledSpecialsForBanner: StoreSpecial[];
  hasLinkedBank?: boolean;
  janeGold: JaneGold;
  openWizard: (source: WizardOpenSource) => void;
  preloadedProductIds: number[];
  rowInstance?: SmartSort<MaterializedMenuProduct>;
  sortOptions: LabeledOption<SortOptions>[];
  storeSpecials: Nullable<StoreSpecial[]>;
}

export const DealsTab = (props: DealsTabProps) => {
  const { featureFlags } = useRootLoaderData();
  const showInlineWizardCta = useShowInlineWizardCta();
  const { skeletonLoader } = useSkeletonLoader();
  const {
    brandSpecials,
    complianceLanguage,
    currentSort,
    customLanguage,
    enabledSpecialsForBanner,
    hasLinkedBank,
    janeGold,
    openWizard,
    preloadedProductIds,
    rowInstance,
    sortOptions,
    storeSpecials,
  } = props;

  const specialsRedesignMenu = featureFlags[FLAGS.c3SpecialsRedesignMenu];
  const useV3StoreSpecials = featureFlags[FLAGS.c3UseV3StoreSpecials];

  const bannerSpecials = useMemo(() => {
    if (!isEmpty(enabledSpecialsForBanner)) {
      return useV3StoreSpecials
        ? enabledSpecialsForBanner
        : sortSpecials([...enabledSpecialsForBanner]);
    }
    return null;
  }, [enabledSpecialsForBanner, useV3StoreSpecials]);
  const showDealsBanner = bannerSpecials?.length && bannerSpecials?.length > 0;

  const totalProductCount = useMemo(() => {
    if (!rowInstance) {
      return 0;
    }

    return rowInstance.totalProducts;
  }, [rowInstance]);

  const hasResults = useMemo(() => {
    if (!rowInstance) {
      return false;
    }

    return rowInstance.products.length > 0;
  }, [rowInstance]);

  if (!rowInstance) {
    return <> </>;
  }

  if (skeletonLoader) {
    return skeletonLoader;
  }

  const onWizardClick = showInlineWizardCta
    ? () => openWizard(WIZARD_SOURCE.BUTTON)
    : undefined;

  return (
    <>
      {showDealsBanner && (
        <DealsBanner
          complianceLanguage={complianceLanguage}
          storeSpecials={bannerSpecials}
        />
      )}
      {storeSpecials && (
        <SpecialsGrid
          className={styles['specialsRowDeals']}
          complianceLanguage={complianceLanguage}
          reservationModeLabels={customLanguage.reservationModeLabels}
          specialsRedesignMenu={specialsRedesignMenu}
          storeSpecials={storeSpecials}
        />
      )}
      <div className={styles['filtersSelectorContainer']}>
        <FilterSelector
          hasLinkedBank={hasLinkedBank}
          availableProductsCount={totalProductCount}
          sortOptions={sortOptions}
          currentSort={currentSort}
          hideLabeledFilters={true}
          onWizardClick={onWizardClick}
          showCompare={featureFlags[FLAGS.c1CompareProducts]}
        />
      </div>
      <main className="main">
        <div className={styles['dealsButtons']}>
          <MobileFilterRow
            onWizardClick={onWizardClick}
            availableProductsCount={totalProductCount}
            sortOptions={sortOptions}
            currentSort={currentSort}
          />
        </div>
        <Typography
          color="inherit"
          data-testid="product-count"
          className="product-count"
        >
          {formatProductCount(totalProductCount)}
        </Typography>

        {!hasResults && (
          <Flex justifyContent="center" alignItems="center" height="200px">
            <Typography
              color="inherit"
              variant="h1-semibold"
              data-testid="no-results"
            >
              No Products Available
            </Typography>
          </Flex>
        )}

        <ProductGrid
          brandSpecials={brandSpecials}
          customLanguage={customLanguage}
          preloadedProductIds={preloadedProductIds}
          rowInstance={rowInstance}
          specials={storeSpecials}
          janeGold={janeGold}
        />
      </main>
    </>
  );
};
